import React, { useState } from 'react';
import { Facebook, Twitter, Linkedin, Mail, Link as LinkIcon, Check } from 'lucide-react';

const ShareButtons = ({ url, title, excerpt, slug }) => {
  const [copied, setCopied] = useState(false);

  // For Facebook/Twitter/LinkedIn: use the backend OG endpoint so crawlers
  // get rich preview metadata (title, description, image) per article.
  // The endpoint redirects real visitors to the SPA page.
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
  const shareableUrl = slug ? `${apiBase}/api/og/article/${slug}` : url;

  const shareUrl = encodeURIComponent(shareableUrl);
  const shareTitle = encodeURIComponent(title);
  const shareExcerpt = encodeURIComponent(excerpt || '');
  // Email & copy link use the human-friendly SPA URL
  const directUrl = encodeURIComponent(url);

  const handlers = [
    {
      name: 'Facebook',
      testId: 'share-facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
      icon: <Facebook size={18} />,
      bg: 'hover:bg-[#1877F2]',
    },
    {
      name: 'X (Twitter)',
      testId: 'share-twitter',
      href: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`,
      icon: <Twitter size={18} />,
      bg: 'hover:bg-[#000000]',
    },
    {
      name: 'LinkedIn',
      testId: 'share-linkedin',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
      icon: <Linkedin size={18} />,
      bg: 'hover:bg-[#0A66C2]',
    },
    {
      name: 'Email',
      testId: 'share-email',
      href: `mailto:?subject=${shareTitle}&body=${shareExcerpt}%0A%0A${directUrl}`,
      icon: <Mail size={18} />,
      bg: 'hover:bg-[#888680]',
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-3 my-8 p-5 bg-[#26282a] border border-[#3f4816]/50 rounded-xl"
      data-testid="share-buttons"
    >
      <span className="text-[#d9fb06] font-semibold uppercase text-sm tracking-wider">
        Partager :
      </span>
      <div className="flex flex-wrap gap-2">
        {handlers.map((h) => (
          <a
            key={h.name}
            href={h.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={h.testId}
            aria-label={`Partager sur ${h.name}`}
            className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#1a1c1b] border border-[#3f4816] text-[#dfddd6] hover:text-white transition-all ${h.bg}`}
          >
            {h.icon}
          </a>
        ))}
        <button
          type="button"
          onClick={copyLink}
          data-testid="share-copy-link"
          aria-label="Copier le lien"
          className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#1a1c1b] border border-[#3f4816] transition-all ${
            copied ? 'text-[#d9fb06] border-[#d9fb06]' : 'text-[#dfddd6] hover:text-[#d9fb06] hover:border-[#d9fb06]'
          }`}
        >
          {copied ? <Check size={18} /> : <LinkIcon size={18} />}
        </button>
      </div>
      {copied && (
        <span className="text-[#d9fb06] text-sm font-semibold" data-testid="copy-confirmation">
          Lien copié !
        </span>
      )}
    </div>
  );
};

export default ShareButtons;
